import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import moment from "moment";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import AssessmentFormPrint, {
  downloadAssessmentFormPdf,
} from "./AssessmentFormPrint";

const toPrintData = (row) => ({
  ...row,
  client_name: `${row.fname || ""} ${row.lname || ""}`.trim(),
});

/** Saved assessments: only the Client Fitness Assessment Form, plus its PDF. */
export default function ViewClientAssesmentInfo() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [measurement, setMeasurement] = useState([]);
  const [printRecord, setPrintRecord] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const printRef = useRef(null);

  const queuePdf = (row) => setPrintRecord(toPrintData(row));

  useEffect(() => {
    if (!printRecord) return;
    let cancelled = false;

    (async () => {
      setPdfLoading(true);
      try {
        await new Promise((r) =>
          requestAnimationFrame(() => setTimeout(r, 200))
        );
        if (cancelled) return;
        await downloadAssessmentFormPdf(
          printRef.current,
          printRecord.client_name || `client_${printRecord.client_id}`
        );
      } catch (err) {
        console.log(err);
      } finally {
        if (!cancelled) {
          setPdfLoading(false);
          setPrintRecord(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [printRecord]);

  useEffect(() => {
    const fromTimeline = (payload) => {
      const client = payload?.client || {};
      const parts = String(client.name || "").trim().split(/\s+/);
      const fname = parts.shift() || "";
      const lname = parts.join(" ");
      return (payload?.timeline || [])
        .filter((item) => item.kind !== "questionnaire")
        .map((item) => ({
          id: item.id,
          client_id: client.id || id,
          fname,
          lname,
          date: item.date,
          ...(item.metrics || {}),
        }));
    };

    const getMeasurements = async () => {
      setLoading(true);
      try {
        let rows = [];
        try {
          const response = await UserServices.getMeasurementsById(id);
          if (response.status === 200 && Array.isArray(response.data?.data)) {
            rows = response.data.data;
          }
        } catch (err) {
          rows = [];
        }

        if (!rows.length) {
          const timeline = await UserServices.gtClientAssessments({ client_id: id });
          rows = fromTimeline(timeline?.data?.data);
        }

        setMeasurement(rows);
      } catch (err) {
        setMeasurement([]);
      } finally {
        setLoading(false);
      }
    };
    getMeasurements();
  }, [id]);

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          {measurement.length ? (
            measurement.map((el) => (
              <div className="card mb-4" key={el.id}>
                <div className="card-header py-3 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 font-20">
                    {`${el.fname || ""} ${el.lname || ""}`.trim() || "Client"}
                    {el.date
                      ? ` · ${moment(el.date).format("DD-MM-YYYY")}`
                      : ""}
                  </h5>
                  <button
                    type="button"
                    className="btn btn-outline-dark btn-sm"
                    onClick={() => queuePdf(el)}
                    disabled={pdfLoading}
                  >
                    <i className="bi bi-file-earmark-pdf me-1" />
                    {pdfLoading ? "Preparing…" : "Download Form"}
                  </button>
                </div>
                <div className="card-body" style={{ overflowX: "auto" }}>
                  <AssessmentFormPrint data={toPrintData(el)} />
                </div>
              </div>
            ))
          ) : (
            <div className="card">
              <div className="card-body">
                <h6 className="fw-bold text-danger mb-0">
                  {loading ? "Loading assessment…" : "No Assessment Found"}
                </h6>
              </div>
            </div>
          )}
        </div>

        {printRecord ? (
          <div className="caf-print-host" aria-hidden="true">
            <div ref={printRef}>
              <AssessmentFormPrint data={printRecord} />
            </div>
          </div>
        ) : null}
      </main>
      <div className={`loader ${loading ? "in" : ""}`}>
        <div className="spinner-border main-spin"></div>
      </div>
    </div>
  );
}
